import React, { useState, useEffect, useMemo } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import PlusMinQty from "../../utilities/PlusMinQty";
import { updateStrategy, getStrategy } from "../../../api/strategyApi";
import { Info, X } from "lucide-react";
import { useToast } from "components/common/custom-toast/Toast";
import { fmtINR } from "../../../util/helper";
import { Checkbox } from "antd";

const defSLTP = ["Comb Prem", "PNL"];

const StrategyRiskModal = ({ show, setShow, strategyData, combPrem = 0, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [isError, seterror] = useState("");
  const [errorMessages, setErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [netQuantity, setnetQuantity] = useState("");
  const [calcVal, setcalcVal] = useState(0);
  const [slValueType, setSlValueType] = useState(0);
  const [tpValueType, setTpValueType] = useState(0);
  const [trailValueType, setTrailValueType] = useState(2);
  const [loading, setLoading] = useState(false);
  const [perLot, setPerLot] = useState(false);
  const [showScaleWarning, setShowScaleWarning] = useState(false);
  const [strategyFullData, setStrategyFullData] = useState(null);
  const toast = useToast()

  useEffect(() => {
    const fetchStrategyFullData = async () => {
      if (strategyData?.id) {
        try {
          setLoading(true);
          const res = await getStrategy(strategyData.id);
          if (res?.code === 200 && res?.data) {
            setStrategyFullData(res.data);

            const initialFormData = {
              sltpType: res.data.rms?.basis === 2 ? "Underlying" : (res.data.rms?.basis === 1 ? "Comb Prem" : "PNL"),
              stopLossAbsolute: res.data.rms?.stopLossInput?.value ?? null,
              takeProfitAbsolute: res.data.rms?.takeProfitInput?.value ?? null,
              trailReferenceAbsolute: res.data.rms?.trailSL?.reference ?? null,
              trailStepAbsolute: res.data.rms?.trailSL?.step ?? null,
              profitLockAt: res.data.rms?.profitLockAt ?? null,
              profitLockSl: res.data.rms?.profitLockSl ?? null,
              profitLockTrailRef: res.data.rms?.profitLockTrail?.reference ?? null,
              profitLockTrailStep: res.data.rms?.profitLockTrail?.step ?? null,
            };

            setFormData(initialFormData);

            const basisInit = initialFormData.sltpType;
            if (basisInit === "Comb Prem") {
              setSlValueType(res.data.rms?.stopLossInput?.valueType ?? 0);
              setTpValueType(res.data.rms?.takeProfitInput?.valueType ?? 0);
              setTrailValueType(res.data.rms?.trailSL?.valueType === 1 ? 1 : 2);
            } else {
              setSlValueType(0);
              setTpValueType(0);
              setTrailValueType(2);
            }
            setPerLot(!!res.data.rms?.perLot);

            setcalcVal(combPrem || 0);
            setnetQuantity(0);
          } else {
            toast.error('Failed to fetch strategy details');
          }
        } catch (error) {
          console.error('Error fetching strategy:', error);
          toast.error('Failed to fetch strategy details');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchStrategyFullData();
  }, [strategyData]);

  useEffect(() => {
    if (strategyFullData?.legConfigs) {
      let buyPremium = 0;
      let sellPremium = 0;
      strategyFullData.legConfigs.forEach(leg => {
        const ltp = parseFloat(leg.ltp || 0);
        const netQty = parseFloat(leg.netQuantity || 0);
        const qty = Math.abs(netQty || parseFloat(leg.quantity || 0) || 0);
        const lotSize = parseFloat(leg.instrument?.lotSize || leg.lotSize || 1);
        const lots = lotSize > 0 ? (qty / lotSize) : 0;
        const sideInput = leg.transactionType || leg.side || (netQty < 0 ? "SELL" : netQty > 0 ? "BUY" : null);
        const side = sideInput ? String(sideInput).toUpperCase() : null;

        if (side) {
          if (perLot) {
            if (side === "BUY") buyPremium += ltp;
            else if (side === "SELL") sellPremium += ltp;
          } else if (lots > 0) {
            if (side === "BUY") buyPremium += ltp * lots;
            else if (side === "SELL") sellPremium += ltp * lots;
          }
        }
      });
      setcalcVal(buyPremium - sellPremium);
    } else {
      setcalcVal(combPrem || 0);
    }
  }, [strategyFullData, perLot, combPrem]);

  // Clear error messages when sltpType changes
  useEffect(() => {
    setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
    seterror("");
  }, [formData.sltpType]);

  const handleReset = () => {
    if (strategyFullData || strategyData) {
      const resetFormData = {
        sltpType: strategyFullData?.rms?.basis === 2 ? "Underlying" : (strategyFullData?.rms?.basis === 1 ? "Comb Prem" : "PNL"),
        stopLossAbsolute: null,
        takeProfitAbsolute: null,
        trailReferenceAbsolute: null,
        trailStepAbsolute: null,
        profitLockAt: null,
        profitLockSl: null,
        profitLockTrailRef: null,
        profitLockTrailStep: null,
      };
      setFormData(resetFormData);
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      const basisInit = resetFormData.sltpType;
      if (basisInit === "Comb Prem") {
        setSlValueType(2);
        setTpValueType(2);
        setTrailValueType(2);
      } else {
        setSlValueType(0);
        setTpValueType(0);
        setTrailValueType(2);
      }
      seterror("");
    }
  };

  const calculateActualPrice = (value, valueType, basePrice, isStopLoss = false) => {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    const val = parseFloat(value);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    let result;
    switch (valueType) {
      case 0:
        result = val;
        break;
      case 1:
        if (formData.sltpType === "Comb Prem" || formData.sltpType === "PNL") {
          result = isStopLoss ? base - (Math.abs(base) * val / 100) : base + (Math.abs(base) * val / 100);
        } else {
          result = base + (base * val / 100);
        }
        break;
      case 2:
        if (formData.sltpType === "Comb Prem" || formData.sltpType === "PNL") {
          result = isStopLoss ? base - val : base + val;
        } else {
          result = isStopLoss ? -val : val;
        }
        break;
      default:
        result = val;
    }

    return result;
  };

  const getBasePrice = () => {
    const basisIsPNL = formData.sltpType === "PNL";
    if (basisIsPNL) {
      return parseFloat(strategyFullData?.totalPnl || strategyData?.totalPnl || 0);
    } else {
      return parseFloat(combPrem || calcVal || 0);
    }
  };

  const basePrice = useMemo(() => getBasePrice(), [formData.sltpType, strategyFullData?.totalPnl, strategyData?.totalPnl, combPrem, calcVal]);
  const slActualPrice = useMemo(() =>
    calculateActualPrice(formData.stopLossAbsolute, slValueType, basePrice, true),
    [formData.stopLossAbsolute, slValueType, basePrice, formData.sltpType, combPrem, calcVal]
  );
  const tpActualPrice = useMemo(() =>
    calculateActualPrice(formData.takeProfitAbsolute, tpValueType, basePrice, false),
    [formData.takeProfitAbsolute, tpValueType, basePrice, formData.sltpType, combPrem, calcVal]
  );
  const trailRefActualPrice = useMemo(() => {
    if (formData.trailReferenceAbsolute === null || formData.trailReferenceAbsolute === undefined || formData.trailReferenceAbsolute === '') {
      return null;
    }
    const val = parseFloat(formData.trailReferenceAbsolute);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailReferenceAbsolute, trailValueType, basePrice]);

  const trailStepActualPrice = useMemo(() => {
    if (formData.trailStepAbsolute === null || formData.trailStepAbsolute === undefined || formData.trailStepAbsolute === '') {
      return null;
    }
    const val = parseFloat(formData.trailStepAbsolute);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailStepAbsolute, trailValueType, basePrice]);

  const handlePerLotChange = (e) => {
    const checked = e.target.checked;
    setPerLot(checked);
    if (checked && (formData.stopLossAbsolute || formData.takeProfitAbsolute)) {
      setShowScaleWarning(true);
    } else if (!checked) {
      setShowScaleWarning(false);
    }
  };

  useEffect(() => {
    if (!formData.stopLossAbsolute && !formData.takeProfitAbsolute) {
      setShowScaleWarning(false);
    }
  }, [formData.stopLossAbsolute, formData.takeProfitAbsolute]);

  const handleFieldChange = (val, name) => {
    if (name != "sltpType") {
      let value = {
        ...formData,
        [name]: name === "sltpType"
          ? val : (!val && val !== 0) ? null : parseFloat(val),
      };

      if (name === "stopLossAbsolute" && (!val || val === 0 || val === null)) {
        value.trailReferenceAbsolute = null;
        value.trailStepAbsolute = null;
        setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
      }

      if ((name === "profitLockAt" || name === "profitLockSl") && (!val || val === 0 || val === null)) {
        value.profitLockTrailRef = null;
        value.profitLockTrailStep = null;
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
      }

      setFormData(value);

      if (name === "stopLossAbsolute") {
        isValidsltp("sl", parseFloat(val));
      }

      if (name === "takeProfitAbsolute") {
        isValidsltp("tp", parseFloat(val));
      }

      if (name === "trailReferenceAbsolute" || name === "trailStepAbsolute") {
        const newRef = name === "trailReferenceAbsolute" ? value.trailReferenceAbsolute : formData.trailReferenceAbsolute;
        const newStep = name === "trailStepAbsolute" ? value.trailStepAbsolute : formData.trailStepAbsolute;
        validateTrailSL(newRef, newStep);
      }

      if (name === "profitLockAt" || name === "profitLockSl") {
        const newAt = name === "profitLockAt" ? value.profitLockAt : formData.profitLockAt;
        const newSl = name === "profitLockSl" ? value.profitLockSl : formData.profitLockSl;
        validateProfitLock(newAt, newSl);
      }

      if (name === "profitLockTrailRef" || name === "profitLockTrailStep") {
        const newRef = name === "profitLockTrailRef" ? value.profitLockTrailRef : formData.profitLockTrailRef;
        const newStep = name === "profitLockTrailStep" ? value.profitLockTrailStep : formData.profitLockTrailStep;
        validateProfitLockTrail(newRef, newStep);
      }
    } else {
      if (formData.sltpType === val) {
        setFormData({ ...formData, sltpType: val });
      } else {
        setFormData({
          "sltpType": val,
          "stopLossAbsolute": null,
          "takeProfitAbsolute": null,
          "trailReferenceAbsolute": null,
          "trailStepAbsolute": null,
          "profitLockAt": null,
          "profitLockSl": null,
          "profitLockTrailRef": null,
          "profitLockTrailStep": null,
        });
        if (val === "Comb Prem") {
          setSlValueType(0);
          setTpValueType(0);
          setTrailValueType(2);
        } else {
          setSlValueType(0);
          setTpValueType(0);
          setTrailValueType(2);
        }
        seterror("");
        setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
        // Reset perLot when switching away from Premium
        if (val !== "Comb Prem") {
          setPerLot(false);
        }
      }
    }
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");
  const onClickSlType = () => {
    const basis = formData?.sltpType;
    if (basis === "PNL") {
      setSlValueType(prev => cycleWithin(prev, [0, 2]));
      return;
    }
    setSlValueType(prev => cycleWithin(prev, [0, 1, 2]));
  };
  const onClickTpType = () => {
    const basis = formData?.sltpType;
    if (basis === "PNL") {
      setTpValueType(prev => cycleWithin(prev, [0, 2]));
      return;
    }
    setTpValueType(prev => cycleWithin(prev, [0, 1, 2]));
  };
  const onClickTrailType = () => {
    const basis = formData?.sltpType;
    if (basis === "PNL") {
      setTrailValueType(2);
      return;
    }
    setTrailValueType(prev => cycleWithin(prev, [2, 1]));
  };

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  const isValidsltp = (type, val) => {
    const currentCombPrem = combPrem || calcVal || 0;
    const setMsg = (ok, customMsg = null) => {
      const basisLabel = formData.sltpType === "PNL" ? "current PNL" : "combined premium";
      if (type === "sl") {
        const msg = customMsg || `Stop Loss should be below ${basisLabel}`;
        setErrorMessages((prev) => ({
          ...prev,
          stopLoss: ok ? "" : msg
        }));
      } else {
        const msg = customMsg || `Target should be above ${basisLabel}`;
        setErrorMessages((prev) => ({
          ...prev,
          target: ok ? "" : msg
        }));
      }
    };

    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(true);
      seterror("");
      return true;
    }

    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(true);
      seterror("");
      return true;
    }

    const actualPrice = type === "sl"
      ? calculateActualPrice(parsedVal, slValueType, getBasePrice(), true)
      : calculateActualPrice(parsedVal, tpValueType, getBasePrice(), false);

    if (actualPrice === null) {
      setMsg(true);
      seterror("");
      return true;
    }

    const basisIsPNL = formData.sltpType === "PNL";

    const reference = basisIsPNL
      ? (strategyFullData?.totalPnl || strategyData?.totalPnl || 0)
      : currentCombPrem;
    const ref = parseFloat(reference);

    if (Number.isNaN(ref)) {
      setMsg(true);
      seterror("");
      return true;
    }

    if (formData.sltpType === "PNL" || formData.sltpType === "Comb Prem") {
      if (type === "sl") {
        const ok = actualPrice < ref;
        seterror(ok ? "" : type);
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice > ref;
        seterror(ok ? "" : type);
        setMsg(ok);
        return ok;
      }
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (!show || !strategyFullData || !formData || Object.keys(formData).length === 0) return;

    if (formData.stopLossAbsolute !== null && formData.stopLossAbsolute !== undefined && formData.stopLossAbsolute !== '') {
      isValidsltp("sl", parseFloat(formData.stopLossAbsolute));
    }

    if (formData.takeProfitAbsolute !== null && formData.takeProfitAbsolute !== undefined && formData.takeProfitAbsolute !== '') {
      isValidsltp("tp", parseFloat(formData.takeProfitAbsolute));
    }

    validateTrailSL(formData.trailReferenceAbsolute, formData.trailStepAbsolute);

    validateProfitLock(formData.profitLockAt, formData.profitLockSl);

    validateProfitLockTrail(formData.profitLockTrailRef, formData.profitLockTrailStep);
  }, [show, formData, slValueType, tpValueType, trailValueType, strategyFullData, combPrem, calcVal]);

  const clickActionSave = async () => {
    if (!isValidsltp("sl", parseFloat(formData.stopLossAbsolute))) {
      return;
    }
    if (!isValidsltp("tp", parseFloat(formData.takeProfitAbsolute))) {
      return;
    }

    if (!validateTrailSL(formData.trailReferenceAbsolute, formData.trailStepAbsolute)) {
      return;
    }

    if (!validateProfitLock(formData.profitLockAt, formData.profitLockSl)) {
      return;
    }

    if (!validateProfitLockTrail(formData.profitLockTrailRef, formData.profitLockTrailStep)) {
      return;
    }

    if (!strategyFullData) {
      toast.error('Strategy data not loaded');
      return;
    }

    setLoading(true);
    try {
      const basis = formData?.sltpType;
      const basisValue = basis === "Comb Prem" ? 1 : 0;

      const norm = (n) => {
        if (n === 0) return 0;
        if (n === undefined || n === null || n === "") return null;
        const p = parseFloat(n);
        return Number.isNaN(p) ? null : p;
      };

      const isTrailNull = (ref, step) => {
        const refVal = norm(ref);
        const stepVal = norm(step);
        return refVal === null && stepVal === null;
      };

      const isValueNull = (val) => norm(val) === null;

      const rmsPayload = {
        basis: basisValue,
        takeProfitInput: isValueNull(formData.takeProfitAbsolute) ? null : {
          valueType: tpValueType ?? 0,
          value: norm(formData.takeProfitAbsolute)
        },
        stopLossInput: isValueNull(formData.stopLossAbsolute) ? null : {
          valueType: slValueType ?? 0,
          value: norm(formData.stopLossAbsolute)
        },
        trailSL: isTrailNull(formData.trailReferenceAbsolute, formData.trailStepAbsolute) ? null : {
          valueType: (trailValueType === 1 ? 1 : 2),
          reference: norm(formData.trailReferenceAbsolute),
          step: norm(formData.trailStepAbsolute)
        },
        profitLockAt: norm(formData.profitLockAt),
        profitLockSl: norm(formData.profitLockSl),
        profitLockTrail: isTrailNull(formData.profitLockTrailRef, formData.profitLockTrailStep) ? null : {
          valueType: 2,
          reference: norm(formData.profitLockTrailRef),
          step: norm(formData.profitLockTrailStep)
        }
      };

      if (basisValue === 1 && perLot) {
        rmsPayload.perLot = true;
      }

      const payload = {
        ...strategyFullData,
        rms: rmsPayload
      };

      const res = await updateStrategy(strategyData.id, payload);
      if (res?.code === 200) {
        toast.success('Strategy risk settings updated successfully');
        if (onSuccess) {
          onSuccess();
        }
        setShow(false);
      } else {
        toast.error(res?.error || 'Failed to update strategy risk settings');
      }
    } catch (error) {
      console.error('Error updating strategy:', error);
      toast.error('Failed to update strategy risk settings');
    } finally {
      setLoading(false);
    }
  };

  const ClearAll = () => {
    setFormData({
      "sltpType": "PNL",
      "stopLossAbsolute": 0,
      "takeProfitAbsolute": 0,
      "trailReferenceAbsolute": 0,
      "trailStepAbsolute": 0,
      "profitLockAt": 0,
      "profitLockSl": 0,
      "profitLockTrailRef": 0,
      "profitLockTrailStep": 0,
    });
    seterror("");
    setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
  };

  const clearfiled = (name) => {
    let updatedFormData = {
      ...formData,
      [name]: null
    };

    if (name === "stopLossAbsolute") {
      updatedFormData.trailReferenceAbsolute = null;
      updatedFormData.trailStepAbsolute = null;
      setErrorMessages((prev) => ({ ...prev, stopLoss: "", trailSL: "" }));
      seterror("");
    }
    else if (name === "profitLockAt" || name === "profitLockSl") {
      updatedFormData.profitLockTrailRef = null;
      updatedFormData.profitLockTrailStep = null;
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    }
    else if (name === "takeProfitAbsolute") {
      setErrorMessages((prev) => ({ ...prev, target: "" }));
      seterror("");
    }
    else if (name === "trailReferenceAbsolute") {
      const otherValue = updatedFormData.trailStepAbsolute;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    else if (name === "trailStepAbsolute") {
      const otherValue = updatedFormData.trailReferenceAbsolute;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    else if (name === "profitLockTrailRef") {
      const otherValue = formData.profitLockTrailStep;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }
    else if (name === "profitLockTrailStep") {
      const otherValue = formData.profitLockTrailRef;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }

    setFormData(updatedFormData);
  };

  if (!strategyData) return null;

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="sm"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="fixed-modal-sltp"
    >
      <Modal.Header style={{ position: 'relative' }}>
        <Modal.Title
          className="modal-title-size"
          id="contained-modal-title-vcenter"
        >
          Risk Settings:<br />
          {strategyFullData?.name || strategyData?.name}
        </Modal.Title>
        <button
          type="button"
          className="modal-close-button"
          onClick={() => setShow(false)}
          aria-label="Close"
        >
          ×
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-sltp">
          <div style={{ justifyContent: "space-between" }} className="sltp-div modal-input">
            <Form.Group className="form-group">
              <label className="form-label">SL/TP Basis</label>
              <Form.Select
                aria-label="Default select example"
                name="sltpType"
                value={formData.sltpType}
                onChange={(e) => handleFieldChange(e.target.value, "sltpType")}
              >
                {defSLTP.map((data, index) => {
                  return (
                    <option value={data} key={index}>
                      {data}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
            {formData.sltpType === "Comb Prem" && (
              <Checkbox
                id="perLot-strategy"
                checked={perLot}
                onChange={handlePerLotChange}
                style={{ fontSize: '11px', marginLeft: '8px', whiteSpace: 'nowrap', width: '100px' }}
              >
                1 Lot RMS
              </Checkbox>
            )}
            <div className="pnl-com-p">
              {formData.sltpType === "PNL" ?
                <p className="m-0">PNL: <span className={(strategyData?.totalPnl || 0) >= 0 ? "text-success" : "text-danger"}>{(strategyFullData?.totalPnl || strategyData?.totalPnl || 0)?.toFixed(2)}</span></p>
                : <p className="m-0">Comb Prem: <span style={{ color: "inherit !important" }}>{(calcVal || 0).toFixed(2)}</span></p>
              }
            </div>
          </div>

          <div className="sltp-div">
            <div className="sltp-col-left">
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Stop Loss
                {slActualPrice !== null && (slValueType === 1 || slValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    ({fmtINR(slActualPrice.toFixed(2))})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                <div className={errorMessages.stopLoss ? "error-border" : ""}>
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "stopLossAbsolute");
                    }}
                    filterValue={formData.stopLossAbsolute}
                    name="stopLossAbsolute"
                    min={null}
                    step={null}
                    max={null}
                    title=""
                    formatINR={true}
                  />
                </div>
                {formData?.sltpType === "PNL" ? (
                  <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(slValueType)}</div>
                ) : (
                  <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(slValueType)}</div>
                )}
                <X
                  size={16}
                  onClick={() => clearfiled("stopLossAbsolute")}
                  className="sltp-delete-icon sltp-delete-icon-enabled"
                  style={{ cursor: 'pointer' }}
                />
              </div>
              {errorMessages.stopLoss && (
                <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                  {errorMessages.stopLoss}
                </div>
              )}
            </div>
            <div className="sltp-col-right">
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Target
                {tpActualPrice !== null && (tpValueType === 1 || tpValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    (₹{tpActualPrice.toFixed(2)})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                <div className={errorMessages.target ? "error-border" : ""}>
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "takeProfitAbsolute");
                    }}
                    filterValue={formData.takeProfitAbsolute}
                    name="takeProfitAbsolute"
                    min={null}
                    step={null}
                    max={null}
                    title=""
                    formatINR={true}
                  />
                </div>
                {formData?.sltpType === "PNL" ? (
                  <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(tpValueType)}</div>
                ) : (
                  <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(tpValueType)}</div>
                )}
                <X
                  size={16}
                  onClick={() => clearfiled("takeProfitAbsolute")}
                  className="sltp-delete-icon sltp-delete-icon-enabled"
                  style={{ cursor: 'pointer' }}
                />
              </div>
              {errorMessages.target && (
                <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                  {errorMessages.target}
                </div>
              )}
            </div>
          </div>

          <div className="sltp-div-profile">
            {/* Stoploss Trailing */}
            <p className="sltp-trail-header">Stoploss Trailing</p>
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">
                  For every X move
                  {trailRefActualPrice !== null && typeof trailRefActualPrice === 'number' && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      (₹{trailRefActualPrice.toFixed(2)})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "trailReferenceAbsolute");
                    }}
                    filterValue={formData.trailReferenceAbsolute || null}
                    name="trailReferenceAbsolute"
                    min={0.05}
                    step={0.05}
                    formatINR={true}
                    disabled={formData.stopLossAbsolute === null}
                  />
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class">{renderPNLLabel()}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                  )}
                  <X
                    size={16}
                    onClick={() => clearfiled("trailReferenceAbsolute")}
                    className={`sltp-delete-icon ${(formData.stopLossAbsolute === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">
                  Trail SL by Y
                  {trailStepActualPrice !== null && typeof trailStepActualPrice === 'number' && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      ({fmtINR(trailStepActualPrice.toFixed(2))})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "trailStepAbsolute");
                    }}
                    filterValue={formData.trailStepAbsolute ?? null}
                    name="trailStepAbsolute"
                    min={0.05}
                    step={0.05}
                    formatINR={true}
                    disabled={formData.stopLossAbsolute === null}
                  />
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class">{renderPNLLabel()}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                  )}
                  <X
                    size={16}
                    onClick={() => clearfiled("trailStepAbsolute")}
                    className={`sltp-delete-icon ${(formData.stopLossAbsolute === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
            {errorMessages.trailSL && (
              <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                {errorMessages.trailSL}
              </div>
            )}

            <p className="sltp-trail-header">Profit Locking</p>
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "profitLockAt");
                    }}
                    filterValue={formData.profitLockAt ?? null}
                    name="profitLockAt"
                    min={null}
                    step={null}
                    max={null}
                    title=""
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X
                    size={16}
                    onClick={() => clearfiled("profitLockAt")}
                    className="sltp-delete-icon sltp-delete-icon-enabled"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">Lock minimum profit at</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "profitLockSl");
                    }}
                    filterValue={formData.profitLockSl ?? null}
                    name="profitLockSl"
                    min={null}
                    step={null}
                    max={null}
                    title=""
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X
                    size={16}
                    onClick={() => clearfiled("profitLockSl")}
                    className="sltp-delete-icon sltp-delete-icon-enabled"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
            {errorMessages.profitLock && (
              <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                {errorMessages.profitLock}
              </div>
            )}

            {/* Profit Trailing */}
            <p className="sltp-trail-header">Profit Trailing</p>
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "profitLockTrailRef");
                    }}
                    filterValue={formData.profitLockTrailRef ?? null}
                    name="profitLockTrailRef"
                    min={0.05}
                    step={0.05}
                    max={null}
                    title=""
                    formatINR={true}
                    disabled={formData.profitLockAt === null || formData.profitLockSl === null}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X
                    size={16}
                    onClick={() => clearfiled("profitLockTrailRef")}
                    className={`sltp-delete-icon ${(formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty, value) => {
                      handleFieldChange(qty, "profitLockTrailStep");
                    }}
                    filterValue={formData.profitLockTrailStep ?? null}
                    name="profitLockTrailStep"
                    min={0.05}
                    step={0.05}
                    max={null}
                    title=""
                    formatINR={true}
                    disabled={formData.profitLockAt === null || formData.profitLockSl === null}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X
                    size={16}
                    onClick={() => clearfiled("profitLockTrailStep")}
                    className={`sltp-delete-icon ${(formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
            {errorMessages.profitLockTrail && (
              <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                {errorMessages.profitLockTrail}
              </div>
            )}
          </div>

          {showScaleWarning && (
            <div className="info-note mb-2" style={{ fontSize: '10px', padding: '8px 10px' }}>
              <Info size={12} style={{ color: '#faad14', marginRight: '4px' }} />
              <span>Scale changed! Please re-verify Absolute SL/TP (Values are now Per Lot).</span>
            </div>
          )}
        </div>
        {/* Summary Section */}
        <div className="sltp-summary-section">
          <div className="sltp-summary-title">
            Summary
          </div>
          <div className="sltp-summary-body">
            {(() => {
              const summaryLines = [];
              const basisLabel = formData?.sltpType === "PNL" ? "PnL" : "combined premium";
              const currentCombPrem = combPrem || calcVal || 0;

              // Stop Loss
              if (formData.stopLossAbsolute !== null && formData.stopLossAbsolute !== undefined && formData.stopLossAbsolute !== '') {
                const slValue = parseFloat(formData.stopLossAbsolute);
                let slText = '';
                if (formData?.sltpType === "PNL") {
                  if (slValueType === 0) {
                    slText = `₹${Math.abs(slValue).toFixed(2)}`;
                  } else {
                    // For Pts, show actual price
                    if (slActualPrice !== null) {
                      slText = `₹${Math.abs(slActualPrice).toFixed(2)}`;
                    } else {
                      slText = `${Math.abs(slValue).toFixed(2)} pts`;
                    }
                  }
                  summaryLines.push(`If ${basisLabel} falls to ${slText}, the strategy will exit.`);
                } else {
                  let slDisplay = '';
                  if (slValueType === 0) {
                    slDisplay = `₹${slValue.toFixed(2)}`;
                  } else if (slValueType === 1 || slValueType === 2) {
                    // For % and Pts, show actual price
                    if (slActualPrice !== null) {
                      slDisplay = `₹${slActualPrice.toFixed(2)}`;
                    } else {
                      slDisplay = slValueType === 1 ? `${slValue.toFixed(2)}%` : `${slValue.toFixed(2)} pts`;
                    }
                  }
                  summaryLines.push(`If ${basisLabel} falls to ${slDisplay}, the strategy will exit.`);
                }
              }

              // Target
              if (formData.takeProfitAbsolute !== null && formData.takeProfitAbsolute !== undefined && formData.takeProfitAbsolute !== '') {
                const tpValue = parseFloat(formData.takeProfitAbsolute);
                let tpText = '';
                if (formData?.sltpType === "PNL") {
                  if (tpValueType === 0) {
                    tpText = `₹${Math.abs(tpValue).toFixed(2)}`;
                  } else {
                    // For Pts, show actual price
                    if (tpActualPrice !== null) {
                      tpText = `₹${Math.abs(tpActualPrice).toFixed(2)}`;
                    } else {
                      tpText = `${Math.abs(tpValue).toFixed(2)} pts`;
                    }
                  }
                  summaryLines.push(`If ${basisLabel} rises to ${tpText}, the strategy will exit with profit.`);
                } else {
                  let tpDisplay = '';
                  if (tpValueType === 0) {
                    tpDisplay = `₹${tpValue.toFixed(2)}`;
                  } else if (tpValueType === 1 || tpValueType === 2) {
                    // For % and Pts, show actual price
                    if (tpActualPrice !== null) {
                      tpDisplay = `₹${tpActualPrice.toFixed(2)}`;
                    } else {
                      tpDisplay = tpValueType === 1 ? `${tpValue.toFixed(2)}%` : `${tpValue.toFixed(2)} pts`;
                    }
                  }
                  summaryLines.push(`If ${basisLabel} rises to ${tpDisplay}, the strategy will exit with profit.`);
                }
              }

              // Stoploss Trailing
              if ((formData.trailReferenceAbsolute !== null && formData.trailReferenceAbsolute !== undefined && formData.trailReferenceAbsolute !== '') &&
                (formData.trailStepAbsolute !== null && formData.trailStepAbsolute !== undefined && formData.trailStepAbsolute !== '')) {
                const refValue = parseFloat(formData.trailReferenceAbsolute);
                const stepValue = parseFloat(formData.trailStepAbsolute);
                let refText = '';
                let stepText = '';

                if (formData?.sltpType === "PNL") {
                  refText = `₹${refValue.toFixed(2)}`;
                  stepText = `₹${stepValue.toFixed(2)}`;
                } else {
                  if (trailValueType === 1) {
                    // For %, show actual price if available
                    if (trailRefActualPrice !== null && trailStepActualPrice !== null) {
                      refText = `₹${trailRefActualPrice.toFixed(2)}`;
                      stepText = `₹${trailStepActualPrice.toFixed(2)}`;
                    } else {
                      refText = `${refValue.toFixed(2)}%`;
                      stepText = `${stepValue.toFixed(2)}%`;
                    }
                  } else {
                    refText = `₹${refValue.toFixed(2)}`;
                    stepText = `₹${stepValue.toFixed(2)}`;
                  }
                }
                summaryLines.push(`For every ${refText} increase in profit, SL will move up by ${stepText}.`);
              }

              // Profit Locking
              if ((formData.profitLockAt !== null && formData.profitLockAt !== undefined && formData.profitLockAt !== '') &&
                (formData.profitLockSl !== null && formData.profitLockSl !== undefined && formData.profitLockSl !== '')) {
                const atValue = parseFloat(formData.profitLockAt);
                const slValue = parseFloat(formData.profitLockSl);
                summaryLines.push(`When profit reaches ₹${atValue.toFixed(2)}, at least ₹${slValue.toFixed(2)} will be protected.`);
              }

              // Profit Trailing
              if ((formData.profitLockTrailRef !== null && formData.profitLockTrailRef !== undefined && formData.profitLockTrailRef !== '') &&
                (formData.profitLockTrailStep !== null && formData.profitLockTrailStep !== undefined && formData.profitLockTrailStep !== '')) {
                const refValue = parseFloat(formData.profitLockTrailRef);
                const stepValue = parseFloat(formData.profitLockTrailStep);
                summaryLines.push(`Each time profit increases by ₹${refValue.toFixed(2)}, locked profit will rise by ₹${stepValue.toFixed(2)}.`);
              }

              if (summaryLines.length === 0) {
                return <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No risk settings configured</div>;
              }

              return summaryLines.map((line, index) => (
                <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>
                  {line}
                </div>
              ));
            })()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleReset} variant="outline-primary">Reset</Button>
        <Button
          disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail || loading}
          onClick={clickActionSave}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StrategyRiskModal;